import React, { useState } from 'react';
import type { GdSessionFeedbackResponse } from '../../../api/gdApi';
import RadarChart from '../../../components/common/RadarChart';
import { skillScoreRawToBarPct, skillScoreRawToOutOf10, skillScoreRawToRadarValue } from '../skillScoreUtils';
import AiEvalScoreLabel from './AiEvalScoreLabel';
import './FeedbackDashboard.css'; // Reuse the same CSS as FeedbackDashboard

interface GdFeedbackDashboardProps {
  data: GdSessionFeedbackResponse;
  onClose: () => void;
}

const DIMENSION_LABELS: Record<string, string> = {
  content_quality: 'Content Quality',
  communication: 'Communication',
  analytical_thinking: 'Analytical Thinking',
  listening_responsiveness: 'Listening',
  initiative_leadership: 'Initiative',
  collaboration: 'Collaboration',
  persuasion_influence: 'Persuasion',
  coherence_structure: 'Coherence',
};

function getScoreColor(raw: number): string {
  const score = skillScoreRawToOutOf10(raw, 0);
  if (score >= 8) return '#10B981';
  if (score >= 6) return '#F59E0B';
  if (score >= 4) return '#EF4444';
  return '#6B7280';
}

function getReadinessColor(level: string): string {
  switch (level.toLowerCase()) {
    case 'excellent':
      return '#10B981';
    case 'proficient':
      return '#22d3ee';
    case 'developing':
      return '#F59E0B';
    case 'needs_improvement':
      return '#EF4444';
    default:
      return '#6B7280';
  }
}

const GdFeedbackDashboard: React.FC<GdFeedbackDashboardProps> = ({ data, onClose }) => {
  const session = data.session;
  const evaluation = data.evaluation || {};
  const [selectedTurn, setSelectedTurn] = useState<number | null>(null);

  // Build radar data from dimensions
  const radarLabels = Object.keys(evaluation.dimensions || {})
    .slice(0, 6)
    .map((k) => DIMENSION_LABELS[k] || k);
  const radarValues = Object.values(evaluation.dimensions || {})
    .slice(0, 6)
    .map((d) => skillScoreRawToRadarValue(d.score, 0));

  // Communication skills for skill cards
  const commSkills = evaluation.communication_skills || { clarity: 0, confidence: 0, engagement: 0, feedback: '' };
  const gdSkills = evaluation.gd_specific_skills || { topic_adherence: 0, counter_argument_handling: 0, group_awareness: 0, feedback: '' };

  const clarityBar = skillScoreRawToBarPct(commSkills.clarity, 0);
  const confidenceBar = skillScoreRawToBarPct(commSkills.confidence, 0);
  const topicBar = skillScoreRawToBarPct(gdSkills.topic_adherence, 0);
  const counterBar = skillScoreRawToBarPct(gdSkills.counter_argument_handling, 0);
  const clarity100 = Math.round(skillScoreRawToRadarValue(commSkills.clarity, 0));
  const confidence100 = Math.round(skillScoreRawToRadarValue(commSkills.confidence, 0));
  const topic100 = Math.round(skillScoreRawToRadarValue(gdSkills.topic_adherence, 0));
  const counter100 = Math.round(skillScoreRawToRadarValue(gdSkills.counter_argument_handling, 0));
  const clarityBody =
    commSkills.clarity_feedback ??
    commSkills.feedback ??
    'Clearness and articulation of ideas during the discussion.';
  const confidenceBody =
    commSkills.confidence_feedback ?? 'Assertiveness and self-assurance when presenting viewpoints.';

  const strengths = evaluation.strengths || [];
  const areasForImprovement = evaluation.areas_for_improvement || [];
  const recommendations = evaluation.recommendations || [];

  const formattedDate = new Date(data.timestamp || Date.now()).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const readinessLevel = evaluation.readiness_level || 'developing';
  const readinessText =
    readinessLevel === 'needs_improvement'
      ? 'Needs Improvement'
      : readinessLevel.charAt(0).toUpperCase() + readinessLevel.slice(1);

  return (
    <div className="feedback-dashboard candidate-overlay">
      <div className="feedback-shell">
        {/* Header */}
        <div className="feedback-header card-glass">
          <div className="header-left">
            <div className="header-info">
              <h1>Group Discussion</h1>
              <div className="interview-details">
                <span className="candidate-name">
                  {session?.difficulty?.charAt(0).toUpperCase()}
                  {session?.difficulty?.slice(1)} · GD
                </span>
                <span className="interview-status">{formattedDate}</span>
              </div>
            </div>
          </div>
          <div className="header-actions">
            <button onClick={onClose} className="close-button">
              ×
            </button>
          </div>
        </div>

        <div className="feedback-main-new">
          {/* Row 1: Topic + Final Score */}
          <div className="feedback-row feedback-row-top">
            <div className="feedback-cell avatar-cell">
              <div className="avatar-chip" style={{ background: 'linear-gradient(135deg, #ee9c22, #d97706)' }}>
                GD
              </div>
              <div>
                <p className="eyebrow">Topic</p>
                <h3 style={{ fontSize: '13px', maxWidth: '280px' }}>{session?.topic || 'Group Discussion'}</h3>
                <p className="subtle-text">
                  {session?.total_turns} turns · {evaluation.participation_metrics?.contribution_ratio} contributions
                </p>
              </div>
            </div>
            <div className="feedback-cell score-cell">
              <div className="overall-score-left">
                <AiEvalScoreLabel />
                <h2 style={{ color: getReadinessColor(readinessLevel) }}>
                  {evaluation.overall_score ?? 0}/100
                </h2>
              </div>
              <p className="score-subtitle">{readinessText}</p>
            </div>
          </div>

          {/* Row 2: Skill Radar + Skill Cards */}
          <div className="feedback-row feedback-row-skills">
            <div className="feedback-cell radar-cell">
              <div className="performance-header">
                <div>
                  <p className="eyebrow">Dimension radar</p>
                  <h3>Performance pulse</h3>
                </div>
              </div>
              <div className="radar-wrapper">
                <RadarChart labels={radarLabels} values={radarValues} showLegend={false} />
              </div>
              <div className="radar-legend">
                {radarLabels.map((label, idx) => (
                  <div key={label} className="legend-item">
                    <span className="legend-key">{String.fromCharCode(97 + idx)}</span>
                    <p>{label}</p>
                    <strong>{Math.round(radarValues[idx])}</strong>
                  </div>
                ))}
              </div>
            </div>
            <div className="feedback-cell skills-cell">
              <div className="skill-grid">
                {/* Communication Clarity */}
                <div className="skill-card">
                  <div className="skill-header">
                    <span>Clarity</span>
                    <strong style={{ color: getScoreColor(commSkills.clarity) }}>{clarity100}/100</strong>
                  </div>
                  <div className="skill-progress">
                    <div className="progress-bar" style={{ width: `${clarityBar}%` }} />
                  </div>
                  <p>{clarityBody}</p>
                </div>

                {/* Confidence */}
                <div className="skill-card">
                  <div className="skill-header">
                    <span>Confidence</span>
                    <strong style={{ color: getScoreColor(commSkills.confidence) }}>{confidence100}/100</strong>
                  </div>
                  <div className="skill-progress">
                    <div className="progress-bar" style={{ width: `${confidenceBar}%` }} />
                  </div>
                  <p>{confidenceBody}</p>
                </div>

                {/* Topic Adherence */}
                <div className="skill-card">
                  <div className="skill-header">
                    <span>Topic Adherence</span>
                    <strong style={{ color: getScoreColor(gdSkills.topic_adherence) }}>{topic100}/100</strong>
                  </div>
                  <div className="skill-progress">
                    <div className="progress-bar" style={{ width: `${topicBar}%` }} />
                  </div>
                  <p>Staying on topic and relevance of contributions.</p>
                </div>

                {/* Counter-Argument Handling */}
                <div className="skill-card">
                  <div className="skill-header">
                    <span>Counter-Arguments</span>
                    <strong style={{ color: getScoreColor(gdSkills.counter_argument_handling) }}>{counter100}/100</strong>
                  </div>
                  <div className="skill-progress">
                    <div className="progress-bar" style={{ width: `${counterBar}%` }} />
                  </div>
                  <p>Ability to address and respond to opposing views.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Row 3: Strengths + Areas to Improve + Recommendations */}
          <div className="feedback-row feedback-row-recommendations">
            <div className="feedback-sections">
              <div className="feedback-section">
                <h3>Key Strengths</h3>
                <ul>
                  {strengths.length > 0 ? (
                    strengths.map((item, idx) => <li key={idx}>{item}</li>)
                  ) : (
                    <li>Focus on the improvement areas to unlock your strengths.</li>
                  )}
                </ul>
              </div>

              <div className="feedback-section">
                <h3>Areas to Sharpen</h3>
                <ul>
                  {areasForImprovement.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>

              <div className="feedback-section">
                <h3>Recommended Next Steps</h3>
                <ul>
                  {recommendations.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Row 4: Performance Summary */}
          <div className="feedback-row feedback-row-transcript">
            <div className="question-card">
              <div className="section-heading">
                <p className="eyebrow">AI evaluation</p>
                <h3>Performance summary</h3>
              </div>
              <p className="feedback-summary-text">{evaluation.performance_summary}</p>
            </div>
          </div>

          {/* Row 5: Detailed Dimension Breakdown */}
          <div className="feedback-row feedback-row-transcript">
            <div className="question-card">
              <div className="section-heading">
                <p className="eyebrow">Detailed breakdown</p>
                <h3>Dimension scores & rationale</h3>
              </div>
              <div className="question-list">
                {Object.entries(evaluation.dimensions || {}).map(([key, dim], idx) => {
                  const dimScore10 = skillScoreRawToOutOf10(dim.score, 0);
                  const dim100 = Math.round(skillScoreRawToRadarValue(dim.score, 0));
                  return (
                  <div key={key} className="question-wrapper">
                    <button
                      className={`question-row ${selectedTurn === idx ? 'active' : ''}`}
                      onClick={() => setSelectedTurn(selectedTurn === idx ? null : idx)}
                    >
                      <span className="question-number">{idx + 1}</span>
                      <span className="question-text">{DIMENSION_LABELS[key] || key}</span>
                      <span
                        className={`quality-pill ${dimScore10 >= 8 ? 'excellent' : dimScore10 >= 6 ? 'good' : dimScore10 >= 4 ? 'fair' : 'poor'}`}
                      >
                        {dim100}/100
                      </span>
                    </button>
                    {selectedTurn === idx && (
                      <div className="question-details">
                        <p className="question-response">
                          <strong>Rationale:</strong> {dim.rationale}
                        </p>
                      </div>
                    )}
                  </div>
                );
                })}
              </div>
            </div>
          </div>

          {/* Row 6: Key Highlights */}
          {evaluation.key_highlights && evaluation.key_highlights.length > 0 && (
            <div className="feedback-row feedback-row-transcript">
              <div className="question-card">
                <div className="section-heading">
                  <p className="eyebrow">Highlights</p>
                  <h3>Your key contributions</h3>
                </div>
                <div className="question-list">
                  {evaluation.key_highlights.map((highlight, idx) => (
                    <div key={idx} className="question-wrapper">
                      <div style={{ padding: '14px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '10px' }}>
                          <span className="question-number">{idx + 1}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{ margin: '0 0 8px', fontSize: '14px', lineHeight: 1.6, color: '#e6eef8' }}>
                              "{highlight.point}"
                            </p>
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                              {highlight.tags.map((tag) => (
                                <span
                                  key={tag}
                                  className={`quality-pill ${tag === 'critical' ? 'excellent' : tag === 'relevant' ? 'good' : 'fair'}`}
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                        <p style={{ margin: 0, fontSize: '13px', lineHeight: 1.6, color: '#98a0a8', paddingLeft: '36px' }}>
                          {highlight.summary}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GdFeedbackDashboard;
